/**
 * EmergencyActionsPanel - Emergency controls for platform operations
 *
 * Features:
 * - Cache invalidation button
 * - Bulk queue pause/resume
 * - Quick maintenance toggle
 * - Confirmation dialogs for destructive actions
 */
import { useState } from 'react';
import { cn } from '@respectly/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import { Button } from '@respectly/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@respectly/ui';
import { motion } from 'framer-motion';
import {
  Zap,
  Trash2,
  PauseCircle,
  PlayCircle,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  RefreshCw,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface EmergencyActionsPanelProps {
  isClearingCache?: boolean;
  lastCacheClearAt?: Date | null;
  pausedQueueCount?: number;
  totalQueueCount?: number;
  onClearCache?: () => Promise<boolean>;
  onPauseAllQueues?: () => Promise<void>;
  onResumeAllQueues?: () => Promise<void>;
  className?: string;
}

type ActionType = 'cache' | 'pause-all' | 'resume-all' | null;

// ============================================
// Helpers
// ============================================

function formatCacheClearTime(date: Date | null | undefined): string {
  if (!date) return 'Never cleared';
  const now = new Date();
  const diff = now.getTime() - new Date(date).getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(minutes / 60);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return new Date(date).toLocaleDateString();
}

// ============================================
// Component
// ============================================

export function EmergencyActionsPanel({
  isClearingCache = false,
  lastCacheClearAt,
  pausedQueueCount = 0,
  totalQueueCount = 0,
  onClearCache,
  onPauseAllQueues,
  onResumeAllQueues,
  className,
}: EmergencyActionsPanelProps) {
  const [confirmAction, setConfirmAction] = useState<ActionType>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recentSuccess, setRecentSuccess] = useState<ActionType>(null);

  const allQueuesPaused = pausedQueueCount > 0 && pausedQueueCount === totalQueueCount;
  const someQueuesPaused = pausedQueueCount > 0 && pausedQueueCount < totalQueueCount;

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      switch (confirmAction) {
        case 'cache':
          if (onClearCache) {
            const success = await onClearCache();
            if (success) setRecentSuccess('cache');
          }
          break;
        case 'pause-all':
          if (onPauseAllQueues) {
            await onPauseAllQueues();
            setRecentSuccess('pause-all');
          }
          break;
        case 'resume-all':
          if (onResumeAllQueues) {
            await onResumeAllQueues();
            setRecentSuccess('resume-all');
          }
          break;
      }
    } finally {
      setIsProcessing(false);
      setConfirmAction(null);
      // Clear success indicator after 3 seconds
      setTimeout(() => setRecentSuccess(null), 3000);
    }
  };

  const getDialogContent = () => {
    switch (confirmAction) {
      case 'cache':
        return {
          title: 'Clear Platform Cache',
          description:
            'This will invalidate all cached data across the platform. Users may experience slower response times while caches rebuild.',
          action: 'Clear Cache',
          icon: Trash2,
        };
      case 'pause-all':
        return {
          title: 'Pause All Queues',
          description:
            'This will pause all background job queues. No new jobs will be processed until queues are resumed. Existing jobs in progress will complete.',
          action: 'Pause All Queues',
          icon: PauseCircle,
        };
      case 'resume-all':
        return {
          title: 'Resume All Queues',
          description:
            'This will resume all paused background job queues. Pending jobs will start processing.',
          action: 'Resume All Queues',
          icon: PlayCircle,
        };
      default:
        return null;
    }
  };

  const dialogContent = getDialogContent();

  return (
    <>
      <Card className={cn('border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900', className)}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
              <Zap className="h-4.5 w-4.5 text-white" />
            </div>
            <div>
              <CardTitle className="text-base">Emergency Actions</CardTitle>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Quick actions for platform operations
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          <div className="space-y-3">
            {/* Cache Clear */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                  <Trash2 className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    Clear Platform Cache
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Last cleared: {formatCacheClearTime(lastCacheClearAt)}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmAction('cache')}
                disabled={isClearingCache}
              >
                {isClearingCache ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : recentSuccess === 'cache' ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-1.5 text-emerald-500" />
                    Cleared
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1.5" />
                    Clear
                  </>
                )}
              </Button>
            </div>

            {/* Queue Controls */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    'h-9 w-9 rounded-lg flex items-center justify-center',
                    pausedQueueCount > 0
                      ? 'bg-amber-100 dark:bg-amber-900/30'
                      : 'bg-slate-200 dark:bg-slate-700'
                  )}
                >
                  {pausedQueueCount > 0 ? (
                    <PauseCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  ) : (
                    <PlayCircle className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    Queue Control
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {allQueuesPaused
                      ? 'All queues paused'
                      : someQueuesPaused
                        ? `${pausedQueueCount} of ${totalQueueCount} queues paused`
                        : `${totalQueueCount} queues running`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {recentSuccess === 'pause-all' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  </motion.div>
                )}
                {recentSuccess === 'resume-all' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  </motion.div>
                )}
                {allQueuesPaused ? (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setConfirmAction('resume-all')}
                  >
                    <PlayCircle className="h-4 w-4 mr-1.5" />
                    Resume All
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmAction('pause-all')}
                  >
                    <PauseCircle className="h-4 w-4 mr-1.5" />
                    Pause All
                  </Button>
                )}
              </div>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Emergency actions affect the entire platform. Use with caution and ensure proper
                communication with your team before executing.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmAction !== null} onOpenChange={() => setConfirmAction(null)}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {dialogContent?.icon && (
                <dialogContent.icon className="h-5 w-5 text-amber-500" />
              )}
              {dialogContent?.title}
            </DialogTitle>
            <DialogDescription>
              {dialogContent?.description}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmAction(null)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isProcessing}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : dialogContent?.icon ? (
                <dialogContent.icon className="h-4 w-4 mr-2" />
              ) : null}
              {dialogContent?.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
